import TrackingEvent, { TrackingProperty } from 'constants/trackingEvents';
import { MAX_COMMUNITY_NAME_LENGTH, useCommunitySwitcher } from 'hooks/useCommunitySwitcher';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { goToHome, routes } from 'utils/routerHelpers';
import { truncateText } from 'utils/textHelpers';
import { isAdmin } from 'utils/userHelpers';

import React, { useMemo } from 'react';

import { Badge } from '@thinkific/chalk/Badge';
import { Text } from '@thinkific/chalk/Text';
import { Gear } from '@thinkific/chalk/react-icons';
import { MenuItemWithSymbols } from '@thinkific/chalk/recipes/MenuItemWithSymbols';
import { Popover } from '@thinkific/chalk/recipes/Popover';

import { useCommunityContext } from 'components/CommunityContext';

import * as css from './CommunitySwitcherPopover.css';

interface CommunitySwitcherPopoverProps {
  children: React.ReactNode;
}

export const CommunitySwitcherPopover = ({ children }: CommunitySwitcherPopoverProps) => {
  const { t } = useTranslation();
  const { trackEvent, community, currentUser } = useCommunityContext();
  const { restOfCommunities } = useCommunitySwitcher();

  const isAdminUser = isAdmin(currentUser);

  const router = useRouter();

  const truncatedCommunityName = useMemo(
    () => truncateText(community.name, MAX_COMMUNITY_NAME_LENGTH),
    [community.name]
  );

  return (
    // Note: The empty span prevents this Popovers high z-index from overlapping the flyout panel on mobile.
    // Should be fixed in the recipe later
    <span>
      <Popover
        title={t('components-topBar_menu', 'Communities switcher menu')}
        trigger={restOfCommunities.length > 0 && children}
        hiddenTitle
      >
        <div>
          <ul className={css.MenuList}>
            {community && (
              <MenuItemWithSymbols
                endSymbol={isAdminUser && <Gear className={css.MenuBadge} size="sm" />}
                label={truncatedCommunityName}
                onClick={(e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
                  if (!isAdminUser) {
                    return;
                  }
                  e.preventDefault();
                  trackEvent(TrackingEvent.GO_TO_COMMUNITIES, {
                    [TrackingProperty.COMPONENT]: 'GlobalHeader',
                  });
                  window.open(routes.editCommunity(community.id), '_blank');
                }}
              />
            )}
            {restOfCommunities.length > 0 && (
              <span className={css.MenuSeparator}>
                <Text color="onLight.text.tertiary" variant="label.sm">
                  {t('components-topBar-menu_title', 'Other communities')}
                </Text>
              </span>
            )}
            {restOfCommunities.map((row) => (
              <MenuItemWithSymbols
                endSymbol={
                  !row.published && (
                    <Badge
                      className={css.MenuBadge}
                      emphasis="medium"
                      label="Draft"
                      variant="neutral"
                    />
                  )
                }
                key={row.id}
                label={row.name}
                onClick={(e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
                  e.preventDefault();
                  trackEvent(TrackingEvent.SWITCH_COMMUNITY, {
                    [TrackingProperty.COMPONENT]: 'GlobalHeader',
                  });
                  goToHome(router, row.id);
                }}
              />
            ))}
          </ul>
        </div>
      </Popover>
    </span>
  );
};
